import React, { useRef, useEffect } from "react";
import { Container } from "@pixi/react";
import { useReelRefs } from "../../Contexts/ReelRefsContext";
import { useSlot } from "../../Contexts/SlotRegistryContext";
import PotCollectionRespinCounterController from "./PotCollectionRespinCounterController";

/**
 * PotCollectionRespinCounter
 *
 * Respin counter for the Pot Collection bonus game.
 * Reuses PotCollectionRespinCounterController since the counter spine
 * and animation logic are identical — same bone structure, same spine asset.
 *
 * Driven imperatively by SpinEngine (not Redux useEffect):
 *   handleSpin             → controller.playAnimation(displayed - 1)   [decrement]
 *   _handleSymbolStopComplete → controller.playAnimation(pendingVal)   [update on coin land]
 */
const PotCollectionRespinCounter = () => {

  const controllerRef = useRef(null);
  const containerRef = useRef(null);

  const { potCollectionRespinCounterRef, pcPlayCounterRef } = useReelRefs();
  const respinCounterSlot = useSlot("gameView.respinCounter");

  // Initialize controller
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      controllerRef.current = new PotCollectionRespinCounterController(
        containerRef.current,
        pcPlayCounterRef,
      );
      potCollectionRespinCounterRef.current = controllerRef.current;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inject into game_view's respin_counter slot via SlotRegistry
  useEffect(() => {
    if (!respinCounterSlot || !containerRef.current) return;
    const node = containerRef.current;
    respinCounterSlot.addChild(node);
    return () => {
      if (node.parent === respinCounterSlot) {
        respinCounterSlot.removeChild(node);
      }
    };
  }, [respinCounterSlot]);

  // Counter animation is driven imperatively by SpinEngine:
  //   handleSpin → controller.playAnimation(displayed - 1)    [decrement on spin start]
  //   _handleSymbolStopComplete → controller.playAnimation(pendingVal) [update on first coin land]
  // No Redux useEffect needed — that would override the imperative timing.

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.cleanup();
        controllerRef.current = null;
      }
      potCollectionRespinCounterRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container
      ref={containerRef}
      name="pot_collection_respin_counter"
      sortableChildren
    />
  );
};

export default PotCollectionRespinCounter;
